import { useMemo } from 'react'

import useQuery from '@api/hooks/useQuery'
import { faBox } from '@fortawesome/pro-light-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
	FormControl,
	InputLabel,
	MenuItem,
	Paper,
	Select,
	Skeleton,
	Stack,
	Typography,
	useTheme,
} from '@mui/material'
import { BarChart } from '@mui/x-charts-pro'
import MarketBasketReportsApi, { MarketBasketReportsKeys } from '@services/marketBasketReports.api'
import { PackConversionDailyStat } from '@services/marketBasketReports.types'
import dayjs from 'dayjs'
import { StringParam, useQueryParam, withDefault } from 'use-query-params'

import { buildDateRange, dateRanges } from './Charts.utils'

export default function PackConversions() {
	const [range, setRange] = useQueryParam(
		'pack-conversion-range',
		withDefault(StringParam, '7-days')
	)

	const { dateRangeStart, dateRangeEnd } = useMemo(() => buildDateRange(range), [range])

	const { data, isLoading } = useQuery({
		queryKey: MarketBasketReportsKeys.packConversions({ dateRangeStart, dateRangeEnd }),
		queryFn: () => MarketBasketReportsApi.packConversions({ dateRangeStart, dateRangeEnd }),
	})

	return (
		<PackConversionsUI data={data || []} isLoading={isLoading} range={range} setRange={setRange} />
	)
}

type PackConversionsUIProps = {
	data: PackConversionDailyStat[]
	isLoading?: boolean
	range: string
	setRange: (range: string) => void
}

export function PackConversionsUI({
	data,
	isLoading = false,
	range,
	setRange,
}: PackConversionsUIProps) {
	const theme = useTheme()
	const isDaily = range === '7-days' || range === '30-days'

	const dataset = useMemo(() => {
		if (!data || data.length === 0) return []

		if (isDaily) {
			return [...data]
				.sort((a, b) => dayjs(a.date).valueOf() - dayjs(b.date).valueOf())
				.map((item) => ({
					date: dayjs(item.date).toDate(),
					reviewedCount: item.reviewedCount,
					appliedCount: item.appliedCount,
				}))
		}

		const weeklyGroups = data.reduce<
			Record<string, { reviewedCount: number; appliedCount: number }>
		>((acc, item) => {
			const weekStart = dayjs(item.date).startOf('week').format('YYYY-MM-DD')
			if (!acc[weekStart]) {
				acc[weekStart] = { reviewedCount: 0, appliedCount: 0 }
			}

			acc[weekStart].reviewedCount += item.reviewedCount
			acc[weekStart].appliedCount += item.appliedCount

			return acc
		}, {})

		return Object.entries(weeklyGroups)
			.sort(([a], [b]) => dayjs(a).valueOf() - dayjs(b).valueOf())
			.map(([weekStart, counts]) => ({
				date: dayjs(weekStart).toDate(),
				reviewedCount: counts.reviewedCount,
				appliedCount: counts.appliedCount,
			}))
	}, [data, isDaily])

	return (
		<Paper elevation={1} sx={{ width: '100%' }} data-testid="pack-conversions-chart-card">
			<Stack p={2} width="100%" direction="row" justifyContent="space-between" alignItems="center">
				<Stack direction="row" spacing={2} p="1rem" alignItems="center">
					<FontAwesomeIcon icon={faBox} size="lg" />
					<Typography variant="h6">Pack Conversions</Typography>
				</Stack>
				<FormControl sx={{ width: '13.75rem' }}>
					<InputLabel>Range</InputLabel>
					<Select
						data-testid="pack-conversions-range-select"
						value={range}
						label="Range"
						onChange={(val) => setRange(val.target.value)}
					>
						{dateRanges.map((option) => (
							<MenuItem key={option.value} value={option.value}>
								{option.label}
							</MenuItem>
						))}
					</Select>
				</FormControl>
			</Stack>

			{isLoading ? (
				<Skeleton
					variant="rectangular"
					width="100%"
					height={500}
					data-testid="pack-conversions-chart-loading"
				/>
			) : (
				<BarChart
					data-testid="pack-conversions-chart"
					dataset={dataset}
					xAxis={[
						{
							dataKey: 'date',
							scaleType: 'band',
							valueFormatter: (v) => dayjs(v as Date).format(isDaily ? 'MMM D' : 'MMM D'),
						},
					]}
					yAxis={[
						{
							width: 50,
							valueFormatter: (v) => Math.round(v).toString(),
						},
					]}
					series={[
						{
							id: 'reviewedCount',
							dataKey: 'reviewedCount',
							label: 'Reviewed Pack Conversions',
							color: theme.palette.primary.main,
						},
						{
							id: 'appliedCount',
							dataKey: 'appliedCount',
							label: 'Auto Corrected Pack Conversions',
							color: theme.palette.secondary.main,
						},
					]}
					height={500}
					borderRadius={8}
					slotProps={{
						legend: {
							position: { vertical: 'bottom' },
							sx: { mb: 2 },
						},
					}}
					grid={{ horizontal: true }}
				/>
			)}
		</Paper>
	)
}
